import { useEffect, useMemo } from "react";

import { DeviceData, DeviceHealthData, Service } from "../../../API";
import useGetAllLocationsDropdownLoading from "../../../common/components/locations/useGetAllLocationsDropdownLoading";
import useLocationFromCache from "../../../common/hooks/useLocationFromCache";
import { useSelectedLocationDropdown } from "../../../common/variables/selectedLocationDropdown";
import { useGetLiveViewData } from "../../data-hub/components/liveview/hooks/useGetLiveViewData";
import { useListenToDeviceHealthDataChange } from "../../deployment-health/hooks/useListenToDeviceHealthDataChange";
import { ServiceTypeEnum } from "../../model-manager/variables/modelManager";
import { useGetLazyPaginatedDevicesMakes } from "../../system-settings/tabs/devices/hooks/useGetLazyPaginatedDevicesMakes";
import {
  DisplayNameInput,
  servicesByLocationVariable,
} from "../variables/devices";
import { useGetServicesByDevices } from "./useGetServicesByDevices";

export interface IModel {
  serviceName: ServiceTypeEnum;
  serviceId: string;
  nodeId: string;
  isRunning?: boolean;
  configuration?: string;
}

export type DeviceDataType = Omit<DeviceData, "__typename">;

export interface IDeviceTableRow {
  rowId: string;
  name: string;
  location: DisplayNameInput;
  node: DisplayNameInput;
  makeModelId: string;
  models: IModel[];
  modelsToRun: string[];
  deviceData: DeviceDataType;
  healthData: Omit<DeviceHealthData, "__typename">;
  isOnline: boolean;
  cameraIpAddress: string;
  rtspTemplate: string;
}

interface IUseDeviceTableRows {
  rows: IDeviceTableRow[];
  loading: boolean;
}

// this hook gets devices by location or tag locations &&& their respective services &&& their respective deviceMakes (to render compatiable models)

// get all devices -> location or customer -> tags
// get all services -> location (does not work for tags!) -> tags
// get all deviceMakes (ok for scale)
export const useDeviceTableRows = (): IUseDeviceTableRows => {
  const selectedLocation = useSelectedLocationDropdown();

  const { locationsDropdownLoading } = useGetAllLocationsDropdownLoading();

  const { devices, loading: deviceLoading, refetch } = useGetLiveViewData();

  const { getCachedLocation } = useLocationFromCache();

  const { data: devicesMakesData, loading: devicesMakesDataLoading } =
    useGetLazyPaginatedDevicesMakes();

  const { data: deviceHealthData } = useListenToDeviceHealthDataChange();

  // Get devices with their node IDs for fetching services
  const devicesWithNodes = useMemo(() => {
    return (
      devices
        ?.map(device => ({
          id: device.id,
          nodeId: device.nodeId,
        }))
        .filter(device => device.id && device.nodeId) ?? []
    );
  }, [devices]);

  // Fetch services for all devices
  const {
    getServicesForDevice,
    allServices,
    loading: servicesLoading,
  } = useGetServicesByDevices(devicesWithNodes);

  console.log({ allServices });

  const isLoading =
    locationsDropdownLoading ||
    deviceLoading ||
    servicesLoading ||
    devicesMakesDataLoading;

  // Update the servicesByLocationVariable with all services for backward compatibility
  useMemo(() => {
    servicesByLocationVariable(allServices as Service[]);
  }, [allServices]);

  const rowsByLocation: IDeviceTableRow[] = useMemo(() => {
    const mappedData = !isLoading
      ? devices?.map(item => {
          // Get services for this specific device

          const deviceServices = getServicesForDevice(item?.id);

          const models = deviceServices.map(service => ({
            serviceName: service?.serviceType as ServiceTypeEnum,
            serviceId: service?.id as string,
            nodeId: service?.nodeId as string,
            isRunning: service?.isRunning ?? false,
          }));

          const cachedLocation = getCachedLocation(item?.locationId ?? "");

          const foundDeviceMake = devicesMakesData?.getDevicesMakes.items.find(
            el => el?.model === item?.makeModelId
          );

          return {
            rowId: item?.id as string,
            name: item?.name as string,
            location: {
              id: item?.locationId as string,
              name: cachedLocation?.name as string,
            },
            node: {
              id: item?.nodeId as string,
              name: item?.nodeName as string,
            },
            modelsToRun: (foundDeviceMake?.modelsToRun ?? []).filter(
              (model): model is string => model !== null
            ),
            makeModelId: item?.makeModelId as string,
            models,
            deviceData: item?.deviceData,
            healthData: item?.healthData ?? "{}",
            isOnline: Boolean(item?.healthData?.isOnline),
            cameraIpAddress: item?.cameraIpAddress as string,
            rtspTemplate: "", // TODO: Add rtspTemplate to Device type if needed
          };
        }) ?? []
      : [];

    let rows = [...mappedData];

    if (selectedLocation?.value) {
      rows = rows?.filter(item => {
        const nameOfLocation = item.location.id;

        return nameOfLocation === selectedLocation?.value;
      });
    }

    return rows;
  }, [
    devices,
    getServicesForDevice,
    window.location.hash,
    isLoading,
    selectedLocation?.value,
  ]);

  // Note: Tag-based service fetching is now handled by useGetServicesByDevices
  // which fetches services for all devices regardless of location or tag filtering

  useEffect(() => {
    return () => {
      // Clear the servicesByLocation variable when the component is unmounted
      servicesByLocationVariable([]);
    };
  }, []);

  const rows = rowsByLocation;

  const memoizedDevicesRows =
    useMemo(() => {
      const isDeviceDataFromSubscription =
        deviceHealthData?.listenToDeviceHealthData;

      if (!isDeviceDataFromSubscription) return rows;

      const updatedRows = rows.map(device => {
        if (isDeviceDataFromSubscription.id === device?.rowId) {
          // Check if the online status has changed
          if (
            isDeviceDataFromSubscription?.healthData?.isOnline !==
            device?.healthData?.isOnline
          ) {
            refetch();
          }

          return {
            ...device,
            isOnline: Boolean(
              isDeviceDataFromSubscription?.healthData?.isOnline
            ),
            models: device.models.map(model => ({
              ...model,
              isRunning:
                getServicesForDevice(device.rowId).find(
                  service => service?.id === model.serviceId
                )?.isRunning ?? false,
            })),
          };
        }

        return {
          ...device,
          models: device.models.map(model => ({
            ...model,
            isRunning:
              getServicesForDevice(device.rowId).find(
                service => service?.id === model.serviceId
              )?.isRunning ?? false,
          })),
        };
      });

      return updatedRows;
    }, [deviceHealthData, rows, getServicesForDevice]) ?? [];

  const sortedRows = [...memoizedDevicesRows]
    ?.sort((a, b) => {
      return a.name.localeCompare(b.name);
    })
    .map((item, i) => ({ ...item, sequenceNumber: i + 1 }));

  return {
    rows: sortedRows,
    loading: isLoading,
  };
};
